import { CrossCircleIcon, TickCircleIcon } from '@/Components/Common/Icons';
import { translator } from '@vantiva-sa/homesight_humaui/Store/configStore';
import moment from 'moment-timezone';
import PropTypes from 'prop-types';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { CallScheduledIcon } from '../../icons';
import './TaskCompliance.less';

const getDateLabel = (dateStr, trans, tz, format = 'YYYY-MM-DD') => {
  const date = moment.tz(dateStr, format, tz);
  const today = moment().tz(tz);
  const yesterday = today.clone().subtract(1, 'day');

  if (date.isSame(today, 'day')) return trans('TODAY');
  if (date.isSame(yesterday, 'day')) return trans('YESTERDAY');
  return date.format('M/D/YYYY');
};
const getStatusInfo = (status, trans) => {
  if (status === 'completed') {
    return {
      text: trans('COMPLETED'),
      className: 'success',
      icon: <TickCircleIcon className="task-table-status-icon" />,
    };
  }

  if (status === 'expired') {
    return {
      text: trans('MISSED'),
      className: 'danger',
      icon: <CrossCircleIcon className="task-table-status-icon" />,
    };
  }

  if (status === 'ongoing') {
    return {
      text: trans('ONGOING'),
      className: 'warning',
      icon: <CallScheduledIcon className="task-table-status-icon" />,
    };
  }

  if (status === 'scheduled') {
    return {
      text: trans('SCHEDULED'),
      className: 'info',
      icon: <CallScheduledIcon className="task-table-status-icon" />,
    };
  }

  return {
    text: status || '',
    className: 'default',
    icon: null,
  };
};

const TaskComplianceTable = ({ tasks, dateStr, timezone, categoryOptions = [] }) => {
  const trans = useSelector(translator);
  const tz = useMemo(() => timezone || moment.tz.guess(), [timezone]);

  if (tasks.length === 0) {
    return <div className="task-table-empty">{trans('NO_TASKS_THIS_DAY')}</div>;
  }

  const dateLabel = getDateLabel(dateStr, trans, tz);

  return (
    <div className="task-table-card">
      <div className="task-table-head">
        <div className="head-cell">{trans('DATE_L')}</div>
        <div className="head-cell">{trans('DESCRIPTION')}</div>
        <div className="head-cell">{trans('STATUS')}</div>
      </div>

      <div className="task-table-body">
        {tasks.map((task, i) => {
          const taskKey = task.timestamp && task.name ? `${task.timestamp}-${task.name}` : i;
          const statusInfo = getStatusInfo(task.status, trans);

          const categoryOption = categoryOptions.find((opt) => opt.value === task.category);

          return (
            <div className="task-table-row" key={taskKey}>
              <div className="row-cell date-cell">
                <span className="date-text">{dateLabel}</span>
                <span className="time-text">{task.time || ''}</span>
              </div>

              <div className="row-cell description-cell">
                {categoryOption?.url ? (
                  <img
                    src={categoryOption.url}
                    alt={task.category}
                    className="task-category-icon"
                  />
                ) : (
                  <span
                    className={`task-description-icon task-desc-icon--${task.status || 'default'}`}
                  >
                    {statusInfo.icon}
                  </span>
                )}
                <span className="task-description-name">{task.name}</span>
              </div>

              <div className="row-cell status-cell">
                <span className={`status-pill ${statusInfo.className}`}>{statusInfo.text}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

TaskComplianceTable.propTypes = {
  tasks: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      status: PropTypes.string,
      time: PropTypes.string,
      timestamp: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      category: PropTypes.string,
    })
  ).isRequired,
  dateStr: PropTypes.string.isRequired,
  timezone: PropTypes.string,
  categoryOptions: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string,
      url: PropTypes.string,
    })
  ),
};

export default TaskComplianceTable;
